// pages/Dashboard.tsx
import React, { useMemo, useState, useCallback } from 'react';
import ProjectList from '../components/ProjectList.tsx';
import { Project, ProjectStatus, FilterCriteria, SortCriteria } from '../types';
import { STATUS_CONFIG, mockUsers } from '../data/mockData.ts';
import { useProjects } from '../context/ProjectContext.tsx';
import { Permissions } from '../constants/permissions.ts';
import { 
  filterProjects, 
  sortProjects, 
  paginateProjects,
  exportToExcel 
} from '../utils/helper.ts';
import { useAuth } from '../context/AuthContext.tsx';
import { useTranslation } from 'react-i18next';
import ProjectListSection from '../components/ProjectListSection.tsx';
import ProjectFilters from '../components/ProjectFilters.tsx';

const PAGE_SIZE = 5;
const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { 
    projects, 
    isLoading, 
    totalCount, 
    currentPage, 
    pageSize, 
    totalPages, 
    filters,
    sort,
    updateFilters,
    updateSort,
    setCurrentPage,
    updateProjectStatus
  } = useProjects();


  const handleExport = () => {
    exportToExcel(projects);
  };

  return (
    <div className="p-6">
      {/* Header with Export Button */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">{t('dashboard.title')}</h1>
          <p className="text-gray-600">{t('dashboard.subtitle')}</p>
        </div>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          {t('dashboard.actions.exportExcel')}
        </button>
      </div>
    
      {/* Search Section */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('dashboard.filters.searchName')}
              </label>
              <input
                type="text"
                value={filters.searchTerm || ''}
                onChange={(e) => updateFilters({ searchTerm: e.target.value })}
                placeholder={t('dashboard.filters.searchName')}
                className="block w-full rounded-md border-gray-300 shadow-sm 
                  focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('dashboard.filters.searchId')}
              </label>
              <input
                type="text"
                value={filters.projectId || ''}
                onChange={(e) => updateFilters({ projectId: e.target.value })}
                placeholder={t('dashboard.filters.searchId')}
                className="block w-full rounded-md border-gray-300 shadow-sm 
                  focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>
      </div>

      <ProjectFilters 
        filters={filters}
        sort={sort}
        onFilterChange={updateFilters}
        onSortChange={updateSort}
      />
      
      <ProjectListSection 
        projects={projects}
        isLoading={isLoading}
        totalCount={totalCount}
        currentPage={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onUpdateProjectStatus={updateProjectStatus}
      />
    </div>
  );
};

export default Dashboard;